import { onDestroy } from 'svelte';
import { get } from 'svelte/store';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { getStores } from '$app/stores';

// Manages a single connection to the SSE stream. connect will return the same connection
// each time, while disconnect will disconnect all such returned instances at once.
//
// Any open connection will be disconnected when the component that created this connection
// unmounts, as we assume that a connection is used by a component and its subcomponents only.
function useRawConnection(url) {
  const { page } = getStores();
  let connection;

  function connect() {
    if (!connection) {
      const $page = get(page);
      const $session = $page.data.session;
      connection = new EventSourcePolyfill(`${$session.env.API_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${$session.token}`,
          'x-cb-current-organization': $session.organization.id,
        },
        // 5 hour time delay to suppress reconnection error see:
        // https://github.com/Yaffle/EventSource/issues/143
        // https://github.com/tserkov/vue-sse/issues/35
        heartbeatTimeout: 18_000_000,
      });
    }
    return connection;
  }

  function disconnect() {
    connection?.close();
    connection = undefined;
  }

  onDestroy(disconnect);

  return { connect, disconnect };
}

// Connects to the SSE stream once a listener is attached, and disconnects once all
// listeners are detached. This provides a nice interface over the guarantees created
// above.
export default function useConnection(url) {
  const { connect, disconnect } = useRawConnection(url);
  const listeners = new Set();

  function removeEventListener(event, listener) {
    const sse = connect();
    sse.removeEventListener(event, listener);
    listeners.delete(listener);
    if (listeners.size === 0) {
      disconnect();
    }
  }

  function addEventListener(event, listener) {
    const sse = connect();
    listeners.add(listener);
    sse.addEventListener(event, listener);
    return () => removeEventListener(event, listener);
  }

  return { addEventListener };
}
